import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Shell from "./Shell";
import { useAdminStyles, brl, num, date } from "./styles";

// Receita. Os três números NÃO são a mesma coisa e a tela mantém isso claro:
//   MRR      = mensalidade contratada (esperada por mês)
//   Recebido = o que entrou de fato no mês
//   Consumo  = uso tarifado, variável
const Revenue = () => {
  const classes = useAdminStyles();
  const history = useHistory();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/revenue", { params: { months: 6 } }).then(r => setD(r.data)).catch(toastError);
  }, []);

  if (!d) return <Shell title="Receita"><div className={classes.empty}>Carregando…</div></Shell>;

  const { serie, mrr, atraso, recusas, topConsumo } = d;
  const maxSerie = serie.reduce((m, s) => Math.max(m, s.recebidoCents + s.consumoCents), 0) || 1;
  const mesLabel = ym => {
    const [y, m] = ym.split("-");
    return `${["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"][Number(m) - 1]}/${y.slice(2)}`;
  };

  return (
    <Shell title="Receita">
      <div className={classes.cards}>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>MRR</div>
          <div className={classes.kpiValue}>{brl(mrr.atualCents)}</div>
          <div className={classes.kpiHint}>{num(mrr.assinaturasAtivas)} assinatura(s) ativa(s)</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Novo MRR (30d)</div>
          <div className={classes.kpiValue}>{brl(mrr.novo30Cents)}</div>
          <div className={classes.kpiHint}>assinaturas iniciadas</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>MRR perdido (30d)</div>
          <div className={classes.kpiValue}>{brl(mrr.perdido30Cents)}</div>
          <div className={classes.kpiHint}>{num(mrr.cancelamentos30)} cancelamento(s)</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Saídas previstas</div>
          <div className={classes.kpiValue}>{num(mrr.cancelamentosAgendados)}</div>
          <div className={classes.kpiHint}>{num(mrr.trocasAgendadas)} troca(s) de plano agendada(s)</div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Entrada por mês</div>
        <div className={classes.cardSub}>
          Recebido de mensalidade e consumo tarifado — separados de propósito, somá-los esconde de onde vem o dinheiro.
        </div>
        {serie.map((s, i) => (
          <div key={s.ym} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
            <div style={{ width: 66 }} className={classes.strong}>{mesLabel(s.ym)}</div>
            <div className={classes.grow}>
              <div className={classes.bar} style={{ display: "flex" }}>
                <div className={classes.barFill} style={{ width: `${(s.recebidoCents / maxSerie) * 100}%`, borderRadius: 0 }} />
                <div className={classes.barFill} style={{ width: `${(s.consumoCents / maxSerie) * 100}%`, borderRadius: 0, opacity: 0.4 }} />
              </div>
            </div>
            <div className={classes.muted} style={{ width: 190, textAlign: "right" }}>
              mensalidade {brl(s.recebidoCents)} · consumo {brl(s.consumoCents)}
            </div>
            <div className={classes.muted} style={{ width: 78, textAlign: "right" }}>+{num(s.novosClientes)} clientes</div>
          </div>
        ))}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Inadimplência</div>
        <div className={classes.cardSub}>Assinaturas em atraso e faturas em aberto — a fila de cobrança.</div>
        {atraso.length === 0 && <div className={classes.empty}>Ninguém em atraso.</div>}
        {atraso.map((a, i) => (
          <div
            key={`${a.userId}-${a.invoiceId}`}
            className={`${classes.row} ${i === 0 ? classes.rowFirst : ""} ${classes.trLink}`}
            onClick={() => history.push(`/admin/users/${a.userId}`)}
          >
            <div className={classes.grow}>
              <div className={classes.strong}>{a.name || a.email} · {a.planName || "—"}</div>
              <div className={classes.muted}>
                vence {date(a.dueAt)} · {a.attempts} tentativa(s)
                {a.lastError ? ` · ${a.lastError}` : ""}
                {a.graceUntil ? ` · carência até ${date(a.graceUntil)}` : ""}
              </div>
            </div>
            <div className={classes.num}>{brl(a.amountCents)}</div>
          </div>
        ))}
      </div>

      <div className={classes.cols}>
        <div className={classes.card}>
          <div className={classes.cardTitle}>Por que as cobranças falham (90d)</div>
          <div className={classes.cardSub}>Motivo informado pelo banco — separa problema nosso de problema do emissor.</div>
          {recusas.length === 0 && <div className={classes.empty}>Nenhuma recusa no período.</div>}
          {recusas.map((r, i) => (
            <div key={r.motivo} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
              <div className={classes.grow}>
                <div className={classes.strong}>{r.motivo}</div>
                <div className={classes.muted}>{brl(r.valorCents)} em jogo</div>
              </div>
              <div className={classes.num}>{num(r.total)}</div>
            </div>
          ))}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>Maiores consumidores (30d)</div>
          <div className={classes.cardSub}>Marcados quando o consumo passa da mensalidade — é onde a margem aperta.</div>
          {topConsumo.length === 0 && <div className={classes.empty}>Sem consumo no período.</div>}
          {topConsumo.map((c, i) => (
            <div
              key={c.userId}
              className={`${classes.row} ${i === 0 ? classes.rowFirst : ""} ${classes.trLink}`}
              onClick={() => history.push(`/admin/users/${c.userId}`)}
            >
              <div className={classes.grow}>
                <div className={classes.strong}>
                  {c.name || c.email}
                  {c.acimaDaMensalidade && (
                    <span className={`${classes.pill} ${classes.warn}`} style={{ marginLeft: 8 }}>acima da mensalidade</span>
                  )}
                </div>
                <div className={classes.muted}>{c.planName || "—"} · mensalidade {brl(c.monthlyPriceCents)}</div>
              </div>
              <div className={classes.num}>{brl(c.consumoCents)}</div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

export default Revenue;
